import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Layout from "../components/layout/Layout";
import { useDestination, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { formatMoney } from "../lib/safeData";
import { MarketplaceLogo, MarketplaceBadge, getMarketplaceConfig } from "../components/MarketplaceLogo";
import LuxuryProductImage from "../components/LuxuryProductImage";
import QuotePreview from "../components/QuotePreview";
import TrendingCarousel from "../components/TrendingCarousel";
import { normalizeCatalogProduct } from "../lib/catalogProduct";
import { useLiveCatalog } from "../hooks/useLiveCatalog";
import { reportCartAdd, reportProductView } from "../lib/analytics";
import { PUBLIC_BRAND } from "../lib/public-copy";

/**
 * Master prompt v3.0 / Rule 2: when the customer pastes a marketplace
 * product link into the unified search bar, we DO NOT search the static
 * catalog - we route the input straight to the dynamic link parser. This
 * predicate decides whether a raw search string is a link.
 */
const SUPPORTED_VENDOR_HOST_RE =
  /(amazon|walmart|temu|shein|alibaba|aliexpress|ebay)\.[a-z.]+$/i;
function looksLikeMarketplaceUrl(raw) {
  if (!raw || typeof raw !== "string") return false;
  const trimmed = raw.trim();
  if (!/^https?:\/\//i.test(trimmed)) return false;
  try {
    const u = new URL(trimmed);
    return SUPPORTED_VENDOR_HOST_RE.test(u.hostname);
  } catch {
    return false;
  }
}
import {
  Search,
  Filter,
  ShoppingCart,
  ExternalLink,
  Package,
  Truck,
  RefreshCw,
  AlertCircle,
  Loader2,
  Bug,
  Radio,
} from "lucide-react";

const marketplaceIds = ["all", "amazon", "alibaba", "aliexpress", "ebay", "temu", "shein", "walmart"];

function ProductCard({ product, destination, currency, onAddToCart, api }) {
  const navigate = useNavigate();
  const mpConfig = getMarketplaceConfig(product.marketplace);

  useEffect(() => {
    reportProductView({
      api,
      productSku: product.product_sku || product.product_id,
      productId: product.product_id,
      marketplace: product.marketplace,
    });
  }, [api, product.product_sku, product.product_id, product.marketplace]);

  const formatPrice = (price) => formatMoney(price, currency);

  // All-inclusive delivered price — no margin comparison exposed to shoppers
  const displayPrice = product.landed_price;

  // Get display title with fallback
  const displayTitle = product.override_title || product.name || `Imported Item from ${product.marketplace}`;
  
  // Check if title is a marketplace name (failed fetch)
  const isBadTitle = ['Amazon.com', 'AliExpress', 'eBay', 'Unknown Product', 'Alibaba.com'].includes(product.name);

  return (
    <div
      className="product-card bg-white border border-slate-200 rounded-lg overflow-hidden group cursor-pointer hover:shadow-xl hover:border-slate-300 transition-all duration-300"
      onClick={() => navigate(`/products/${product.product_id}`)}
      data-testid={`product-card-${product.product_id}`}
    >
      {/* Image */}
      <div className="aspect-square bg-slate-50 relative overflow-hidden">
        <LuxuryProductImage
          product={product}
          alt={displayTitle}
          testId={`product-image-${product.product_id}`}
        />
        {/* Marketplace badge */}
        <div className="absolute top-2 left-2 z-10 pointer-events-none">
          <MarketplaceBadge marketplace={product.marketplace} size="xs" showLabel={false} />
        </div>
        {/* Quick Add Overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <Button
            size="sm"
            className="bg-white text-slate-900 hover:bg-slate-100 shadow-lg"
            onClick={(e) => {
              e.stopPropagation();
              onAddToCart(product.product_id);
            }}
          >
            <ShoppingCart className="h-4 w-4 mr-1" />
            Quick Add
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-slate-900 mb-1 line-clamp-2 min-h-[48px] text-sm">
          {isBadTitle ? `Imported Item from ${product.marketplace?.charAt(0).toUpperCase() + product.marketplace?.slice(1)}` : displayTitle}
        </h3>
        <p className="text-xs text-slate-500 mb-3">{product.category || 'General'}</p>

        {/* Delivered price */}
        <div className="mb-4 p-3 bg-slate-50 rounded-lg">
          <div className="flex items-baseline justify-between">
            <span className="text-xs font-medium text-emerald-700">Your delivered price:</span>
            <span className="text-xl font-bold text-emerald-700">
              {formatPrice(displayPrice)}
            </span>
          </div>
          <p className="mt-2 text-xs text-slate-500 flex items-center gap-1">
            <Truck className="h-3 w-3 text-emerald-500 shrink-0" />
            {PUBLIC_BRAND.dutiesIncluded} to {destination}
          </p>
        </div>

        <div className="flex items-center gap-1 text-xs text-slate-500 mb-3">
          <Truck className="h-3 w-3" />
          <span>All fees included to {destination}</span>
        </div>

        {/* Add to Cart */}
        <Button
          className="w-full bg-orange-500 hover:bg-orange-600 rounded-md text-white"
          onClick={(e) => {
            e.stopPropagation();
            onAddToCart(product.product_id);
          }}
          data-testid={`add-to-cart-${product.product_id}`}
        >
          <ShoppingCart className="h-4 w-4 mr-2" />
          Add to Cart
        </Button>
      </div>
    </div>
  );
}

function ProductSkeleton() {
  return (
    <div className="container-card overflow-hidden">
      <Skeleton className="aspect-square" />
      <div className="p-4 space-y-3">
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-10 w-full" />
      </div>
    </div>
  );
}

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { destination, currency } = useDestination();
  const navigate = useNavigate();

  const [fallbackProducts, setFallbackProducts] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);

  // Filters (must be declared before useLiveCatalog)
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [marketplace, setMarketplace] = useState(
    searchParams.get("marketplace") || "all"
  );
  const [category, setCategory] = useState(searchParams.get("category") || "all");

  // Keep filters in sync with URL (desktop header search, footer marketplace links, etc.)
  useEffect(() => {
    const nextSearch = searchParams.get("search") || "";
    const nextMarketplace = searchParams.get("marketplace") || "all";
    const nextCategory = searchParams.get("category") || "all";
    setSearch((prev) => (prev === nextSearch ? prev : nextSearch));
    setMarketplace((prev) => (prev === nextMarketplace ? prev : nextMarketplace));
    setCategory((prev) => (prev === nextCategory ? prev : nextCategory));
  }, [searchParams]);

  const {
    products: liveProducts,
    total: liveTotal,
    categories: liveCategories,
    showcasePolicy,
    catalogMeta,
    loading,
    refreshing,
    error: liveError,
    refresh: refreshCatalog,
  } = useLiveCatalog({
    api: API,
    destination,
    currency,
    marketplace,
    category,
    search,
  });

  const products = usingFallback ? fallbackProducts?.products || [] : liveProducts;
  const total = usingFallback ? fallbackProducts?.total || 0 : liveTotal;
  const categories = usingFallback ? fallbackProducts?.categories || [] : liveCategories;

  // Master prompt v3.0: dynamic concierge quote for pasted marketplace links.
  // When the search input is a URL we render the live quote ABOVE the grid
  // and skip the catalog filter (the catalog is a static fixture - the real
  // commerce surface is the dynamic parser).
  const [pastedLink, setPastedLink] = useState(null);
  useEffect(() => {
    setPastedLink(looksLikeMarketplaceUrl(search) ? search.trim() : null);
  }, [search]);
  
  // Debug mode: add ?debug=1 to URL to see diagnostics
  const debugMode = searchParams.get("debug") === "1";

  const loadFallbackCatalog = useCallback(async () => {
    const base = typeof window !== "undefined" ? window.location.origin : "";
    const res = await fetch(`${base}/products.json`, {
      headers: { "Cache-Control": "no-cache" },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const all = Array.isArray(data)
      ? data
      : Array.isArray(data?.products)
        ? data.products
        : [];
    let filtered = all;
    if (marketplace && marketplace !== "all") {
      filtered = filtered.filter(
        (p) => (p.marketplace || "").toLowerCase() === marketplace.toLowerCase()
      );
    }
    if (category && category !== "all") {
      filtered = filtered.filter(
        (p) => (p.category || "").toLowerCase() === category.toLowerCase()
      );
    }
    if (search?.trim()) {
      const q = search.trim().toLowerCase();
      filtered = filtered.filter(
        (p) =>
          (p.name || "").toLowerCase().includes(q) ||
          (p.category || "").toLowerCase().includes(q)
      );
    }
    const normalized = filtered.map((p) =>
      normalizeCatalogProduct(p, { destination, currency })
    );
    const cats = [...new Set(all.map((p) => p.category).filter(Boolean))].sort();
    return { products: normalized, total: normalized.length, categories: cats };
  }, [destination, currency, marketplace, category, search]);

  useEffect(() => {
    if (!liveError || liveProducts.length > 0) {
      setUsingFallback(false);
      return;
    }
    let cancelled = false;
    loadFallbackCatalog()
      .then((fb) => {
        if (!cancelled) {
          setFallbackProducts(fb);
          setUsingFallback(true);
        }
      })
      .catch((err) => {
        console.warn("[Products] Offline fallback failed:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [liveError, liveProducts.length, loadFallbackCatalog]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (search) {
      params.set("search", search);
    } else {
      params.delete("search");
    }
    setSearchParams(params);
  };

  const handleAddToCart = async (productId) => {
    try {
      await axios.post(
        `${API}/cart/add`,
        { product_id: productId, quantity: 1 },
        { withCredentials: true }
      );
      toast.success("Added to cart");
      const product = products.find((p) => p.product_id === productId);
      if (product) {
        reportCartAdd({
          api: API,
          productSku: product.product_sku || product.product_id,
          productId: product.product_id,
          marketplace: product.marketplace,
          quantity: 1,
        });
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Failed to add to cart");
    }
  };

  return (
    <Layout>
      {/* Master prompt v2.1 / Phase 4: trending carousel sits positionally
          locked above the catalog grid. Renders nothing when the
          weekly_trending_high_ticket view is empty - so it adds zero
          vertical drift to the layout during low-engagement weeks. */}
      <TrendingCarousel
        api={API}
        destination={destination}
        currency={currency}
        limit={12}
      />
      {pastedLink && (
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 mb-2">
          <QuotePreview
            api={API}
            targetExternalUrl={pastedLink}
            destinationRegion={destination}
            currency={currency}
            onClose={() => {
              const params = new URLSearchParams(searchParams);
              params.delete("search");
              setSearchParams(params);
              setSearch("");
              setPastedLink(null);
            }}
          />
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="products-title">
            Shop Global Products
          </h1>
          <p className="text-slate-600">
            {total} products available • All prices include shipping, customs & taxes to {destination}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <span
              className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
                usingFallback
                  ? "bg-amber-100 text-amber-800"
                  : "bg-emerald-100 text-emerald-800"
              }`}
            >
              <Radio className={`h-3 w-3 ${refreshing ? "animate-pulse" : ""}`} />
              {usingFallback ? "Offline catalog snapshot" : "Live catalog"}
              {catalogMeta?.updatedAt && !usingFallback && (
                <span className="opacity-75">
                  · updated {new Date(catalogMeta.updatedAt).toLocaleString()}
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={refreshCatalog}
              className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-orange-600"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
          <p className="text-xs text-emerald-700 mt-2" data-testid="catalog-trust-banner">
            <span className="text-emerald-700 font-medium">
              ✨ Guaranteed Delivered Price (All duties, taxes &amp; clearance included)
            </span>
          </p>
          {showcasePolicy && (
            <p className="text-xs text-slate-500 mt-2 max-w-3xl">{showcasePolicy}</p>
          )}
        </div>

        {/* Marketplace Quick Filters — horizontal scroll on mobile */}
        <div className="flex gap-2 sm:gap-3 mb-6 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-none">
          {marketplaceIds.map((mpId) => {
            const config = mpId === 'all' 
              ? { name: 'All Marketplaces', bgColor: 'bg-slate-600', brandColor: '#475569' }
              : getMarketplaceConfig(mpId);
            const isSelected = marketplace === mpId;
            
            return (
              <button
                key={mpId}
                onClick={() => setMarketplace(mpId)}
                className={`shrink-0 px-3 sm:px-4 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                  isSelected
                    ? `${config.bgColor} text-white shadow-lg`
                    : "bg-white border border-slate-200 text-slate-700 hover:border-slate-400 hover:shadow"
                }`}
                data-testid={`filter-${mpId}`}
              >
                {mpId !== 'all' && (
                  <MarketplaceLogo 
                    marketplace={mpId} 
                    size="sm" 
                    inverted={isSelected} 
                  />
                )}
                {mpId === 'all' ? config.name : ''}
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600" data-testid="search-button">
              Search
            </Button>
          </form>

          {/* Category Filter */}
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[180px]" data-testid="category-filter">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Debug Mode Banner - Only visible with ?debug=1 */}
        {debugMode && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-300 rounded-lg text-sm font-mono">
            <div className="flex items-center gap-2 mb-2 font-bold text-yellow-800">
              <Bug className="h-4 w-4" />
              Debug Mode Active
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-yellow-900">
              <div><strong>API_BASE:</strong> {API}</div>
              <div><strong>Endpoint:</strong> /products</div>
              <div><strong>Loading:</strong> {loading ? 'Yes' : 'No'}</div>
              <div><strong>Refreshing:</strong> {refreshing ? 'Yes' : 'No'}</div>
              <div><strong>Error:</strong> {liveError ? liveError.message : 'None'}</div>
              <div><strong>Products:</strong> {products.length}</div>
              <div><strong>Fallback:</strong> {usingFallback ? 'Yes' : 'No'}</div>
              <div><strong>Catalog rev:</strong> {catalogMeta?.revision ?? '—'}</div>
            </div>
            {liveError && (
              <div className="mt-2 text-red-700">
                <strong>Error Details:</strong> {liveError.message}
              </div>
            )}
          </div>
        )}

        {/* Products Grid - FIXED state handling */}
        {loading ? (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <ProductSkeleton key={i} />
              ))}
            </div>
          </div>
        ) : liveError && products.length === 0 ? (
          <div className="text-center py-16" data-testid="error-state">
            <AlertCircle className="h-16 w-16 mx-auto text-red-400 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              Cannot reach live catalog
            </h3>
            <p className="text-slate-600 mb-2 max-w-md mx-auto">
              {liveError.message || "Our servers may be starting up. Tap retry to try again."}
            </p>
            <Button
              onClick={refreshCatalog}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="retry-button"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Tap to Retry
            </Button>
          </div>
        ) : products.length > 0 ? (
          /* SUCCESS STATE - Products loaded */
          <div className="grid grid-cols-1 min-[420px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6" data-testid="products-grid">
            {products.map((product) => (
              <ProductCard
                key={product.product_id}
                product={product}
                destination={destination}
                currency={currency}
                onAddToCart={handleAddToCart}
                api={API}
              />
            ))}
          </div>
        ) : !loading ? (
          /* True Empty State - HTTP 200 but zero products returned */
          <div className="text-center py-16" data-testid="empty-state">
            <Package className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              No products found
            </h3>
            <p className="text-slate-600 mb-4">
              Try adjusting your search or filters
            </p>
            {debugMode && (
              <p className="text-xs text-slate-400">
                (API returned 200 OK with 0 products)
              </p>
            )}
          </div>
        ) : (
          /* Initial loading state */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <ProductSkeleton key={i} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
